import type { OktaAuth } from '@okta/okta-auth-js';
import { useOktaAuth } from '@okta/okta-react';

import { getOktaClientId, getOktaIssuer, getOktaRedirectUri } from '@/config/runtimeConfig';
import type { AuthConfig } from '@/contexts/auth/types/authTypes';
import { NEXUS_ROLES } from '@/shared/constants/roles';

export type { AuthConfig } from './auth/types/authTypes';

export function useAuth() {
  const { authState, oktaAuth } = useOktaAuth();

  const loading = authState === null;
  const isAuthenticated = authState?.isAuthenticated ?? false;

  const rawRoles = authState?.accessToken?.claims?.nexus_roles;
  const userRoles: string[] = Array.isArray(rawRoles)
    ? rawRoles.filter((r): r is string => typeof r === 'string')
    : typeof rawRoles === 'string'
      ? [rawRoles]
      : [];

  const hasRole = (role: string) => userRoles.includes(role);
  const isAdminManager = hasRole(NEXUS_ROLES.ADMIN_MANAGER);
  const isAdminAccount = hasRole(NEXUS_ROLES.ADMIN_ACCOUNT);

  const getAccessToken = async () => oktaAuth.getAccessToken();
  const login = () => oktaAuth.signInWithRedirect({ originalUri: '/' });
  const logout = () => oktaAuth.signOut();

  const claims = authState?.idToken?.claims;
  const userInfo = claims ? (claims as Record<string, unknown>) : null;

  return {
    okta: oktaAuth as OktaAuth | null,
    isAuthenticated,
    userInfo,
    userRoles,
    login,
    logout,
    loading,
    getAccessToken,
    error: null as string | null,
    hasRole,
    isAdminManager,
    isAdminAccount,
  };
}

export function buildAuthConfigFromRuntime(): AuthConfig | null {
  const issuer = getOktaIssuer();
  const clientId = getOktaClientId();
  const redirectUri = getOktaRedirectUri() || window.location.origin + '/login/callback';
  if (!issuer || !clientId) return null;
  return {
    issuer,
    clientId,
    redirectUri,
  };
}
